import React, { useEffect, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import { useSuperAdminTenant } from "../context/SuperAdminTenantContext";
import saApi from "../lib/saApi";
import { HardDrive, Database, Rows3, Loader2 } from "lucide-react";

const fmtBytes = (b) => {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0; let n = Number(b);
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n < 10 ? 2 : 1)} ${u[i]}`;
};

const fmtNum = (n) => (n ?? 0).toLocaleString();

export default function SAStorage() {
  const { tenantId } = useSuperAdminTenant();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (tenantId && tenantId !== "all") params.tenant_id = tenantId;
    saApi.get("/storage", { params })
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [], total: { datasets: 0, bytes: 0, rows: 0 } }))
      .finally(() => setLoading(false));
  }, [tenantId]);

  return (
    <SuperAdminShell title="Storage & Cost">
      <div className="px-5 md:px-8 py-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Kpi label="Datasets" value={fmtNum(data?.total?.datasets)} icon={Database} accent="#34D399" />
          <Kpi label="Total Rows" value={fmtNum(data?.total?.rows)} icon={Rows3} accent="#A78BFA" />
          <Kpi
            label="Total Storage"
            value={fmtBytes(data?.total?.bytes || data?.total?.bytes_estimated || 0)}
            icon={HardDrive}
            accent="#22D3EE"
            hint={!data?.total?.bytes && data?.total?.bytes_estimated ? "estimated from row × column heuristic" : null}
          />
        </div>

        <div className="rounded-2xl border border-white/10 overflow-hidden backdrop-blur-md" style={{ background: "rgba(255,255,255,0.02)" }}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10 bg-white/5">
                <th className="px-4 py-3">Tenant</th>
                <th className="px-4 py-3 text-right">Datasets</th>
                <th className="px-4 py-3 text-right">Rows</th>
                <th className="px-4 py-3 text-right">Storage</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr><td colSpan={4} className="px-4 py-6 text-white/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              ) : (data?.items || []).length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-white/40">No data</td></tr>
              ) : data.items.map((i) => (
                <tr key={i.tenant_id} data-testid="sa-storage-row" className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-4 py-2 text-white">{i.tenant_name}</td>
                  <td className="px-4 py-2 text-right text-white/80">{fmtNum(i.datasets)}</td>
                  <td className="px-4 py-2 text-right text-white/80">{fmtNum(i.rows)}</td>
                  <td className="px-4 py-2 text-right text-cyan-300">
                    {fmtBytes(i.bytes || i.bytes_estimated || 0)}
                    {!i.bytes && i.bytes_estimated ? (
                      <span className="ml-1 text-[10px] text-white/40 uppercase tracking-widest">est</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SuperAdminShell>
  );
}

function Kpi({ label, value, icon: Icon, accent, hint }) {
  return (
    <div className="rounded-2xl p-5 border border-white/10 backdrop-blur-md"
         style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))" }}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-white/50">{label}</div>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}22` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
      </div>
      <div className="mt-3 text-3xl font-display text-white">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-white/40">{hint}</div>}
    </div>
  );
}
